/**
 * V70 타자왕 — 한글 단어 사전 (사전 v4)
 *
 * ## 변경 이력 (v3 → v4, 2026-10-19)
 *   - 16개 단어 삭제 (명시 6개 + 부자연 합성어 10개)
 *   - 명시: 휴일수당, 파전요리, 수의사진료, 빵집매장, 서점매장, 워크숍여행
 *   - 부자연: 곰탕국, 백화점매장, 편의점매장, 전골요리, 카페공간, 회식모임,
 *            친환경세제품목, 공기정화식물원, 전통한식당방문, 해물전문요리점, 계절제철과일점
 *   - FE/BE 완전 동일 소스 유지 — 서버 재계산 호환성
 *
 * ## 변경 이력 (v2 → v3)
 *   - 일상어 중심으로 360개 추가
 *
 * ## 구성 방향
 *   일상·자연·음식·문화   ≈ 다수
 *   회사·사무·조직·업무   ≈ 보조
 *   IT·소프트웨어·게임    ≈ 보조
 *   비율은 권장 기준이며, 단어의 자연스러움과 난이도를 우선한다.
 *   접미사 중복 (매장/요리/진료/모임/공간 등) 억지 합성어 금지.
 *
 * ## 난이도 기준
 *   easy   : 2~3글자  / 일상적이고 친숙한 단어            / 100점
 *   medium : 4~6글자  / 일반 명사·복합 명사·업무 용어      / 150점
 *   hard   : 7~10글자 / 긴 복합 명사·표준 전문용어         / 200점
 *
 * ## 선정 기준
 *   - 한글 완성형만 사용 (영문·숫자·기호·공백 금지)
 *   - 띄어쓰기 없이 통용되는 표기만 사용
 *   - 욕설·비속어·차별·혐오·성적 표현 금지
 *   - 오타·비표준 표기·의미가 불분명한 임의 조합어 금지
 *   - 접미사 중복/억지 합성어 금지 (v4 정리 기준)
 *   - 1글자 단어 금지 / 11글자 이상 금지
 *
 * ## 버전 관리 (Phase 3 서버 재계산 호환성)
 *   `WORD_DICTIONARY_VERSION`          — 사전 내용이 바뀌면 올린다.
 *   engine.ts `SPAWN_ALGORITHM_VERSION` — 단어 생성 알고리즘이 바뀌면 올린다.
 */

/** 사전 내용 버전. 단어를 추가·삭제·교체·재분류하면 올린다. */
export const WORD_DICTIONARY_VERSION = 4;

export type Difficulty = 'easy' | 'medium' | 'hard';

/** 단어 분야 (보고·검증용 분류. 런타임 로직에는 쓰이지 않는다) */
export type WordCategory = 'daily' | 'office' | 'it';

// ============================================================
// EASY — 2~3글자 / 100점 (188개)
// ============================================================
export const EASY_WORDS = [
  '사과', '사람', '학교', '시간', '하늘', '구름', '바다', '나무', '바람', '친구',
  '가족', '사랑', '행복', '잔디', '소리', '음악', '영화', '그림', '아침', '점심',
  '저녁', '오늘', '내일', '건강', '운동', '산책', '수영', '요가', '등산', '여행',
  '휴식', '커피', '우유', '김치', '라면', '과일', '채소', '여름', '가을', '겨울',
  '안개', '천둥', '무지개', '달리기', '회사', '출근', '사무실', '회의실', '화면', '파일',
  '컴퓨터', '마우스', '책상', '의자', '침대', '베개', '이불', '세탁기', '냉장고', '전등',
  '선풍기', '신발', '양말', '지갑', '가방', '모자', '안경', '거울', '수건', '치약',
  '비누', '샴푸', '그릇', '접시', '쟁반', '옷걸이', '된장', '간장', '설탕', '소금',
  '후추', '녹차', '홍차', '사탕', '과자', '토스트', '비빔밥', '짬뽕', '치킨', '피자',
  '햄버거', '샐러드', '요거트', '두부', '김밥', '떡볶이', '갈비탕', '설렁탕', '육개장', '삼계탕',
  '불고기', '감자탕', '칼국수', '수제비', '물냉면', '회덮밥', '해장국', '빈대떡', '강아지', '고양이',
  '호랑이', '토끼', '코끼리', '다람쥐', '사슴', '돌고래', '오징어', '거북이', '비둘기', '참새',
  '올빼미', '금붕어', '잠자리', '햄스터', '공룡', '펭귄', '꽃밭', '풀잎', '모래', '호수',
  '숲속', '폭포', '계곡', '서리', '이슬', '별빛', '보름달', '번개', '파도', '노을',
  '새벽', '밤하늘', '산정상', '함박눈', '버스', '택시', '기차', '자전거', '신호등', '지하철',
  '자동차', '비행기', '정거장', '승강장', '공원', '식당', '카페', '서점', '극장', '병원',
  '약국', '은행', '우체국', '미용실', '편의점', '백화점', '시장', '마트', '공항', '커피숍',
  '월급', '동료', '팀장', '명함', '서류', '결재', '보고', '회의', '야근', '회식',
  '독서', '요리', '사진', '서예', '피아노', '기타', '낚시', '야구',
] as const;

// ============================================================
// MEDIUM — 4~6글자 / 150점 (237개)
// ============================================================
export const MEDIUM_WORDS = [
  '지하철역', '아침운동', '저녁식사', '주말여행', '가족사진', '겨울바다', '봄나들이', '가을단풍', '여름휴가', '등산장비',
  '자전거길', '야외공연', '미술전시', '영화관람', '독서모임', '요리교실', '반려동물', '공원산책', '해외여행', '국내여행',
  '건강검진', '수면시간', '아침식사', '과일주스', '전통시장', '지역축제', '사진촬영', '악기연주', '엘리베이터', '야외바비큐',
  '실내운동장', '지하철노선', '겨울스포츠', '천연기념물', '가족나들이', '해돋이명소', '지역문화축제', '자연사박물관', '미술관전시회', '도서관자료실',
  '실내암벽등반', '세계문화유산', '휴가신청', '근태관리', '조직구성', '인사이동', '급여명세', '업무보고', '출장신청', '사내교육',
  '경비정산', '재택근무', '관리시스템', '복지포인트', '성과평가서', '조직개편안', '인사평가제도', '사내복지제도', '신입사원교육', '프로그램',
  '네트워크', '알고리즘', '엔지니어', '디자이너', '프로젝트', '운영체제', '타자연습', '게임대회', '최고점수', '리더보드',
  '순위발표', '소프트웨어', '인터페이스', '가상메모리', '데이터센터', '통합테스트', '데이터베이스', '통합개발환경', '기계학습모델', '컨테이너기술',
  '전자레인지', '청소도구', '화장품정리', '방충망설치', '커튼봉설치', '빨래건조대', '밥솥보온', '에어컨리모컨', '공기청정기', '소형가전',
  '주방용품', '거실가구', '침실가구', '욕실용품', '세면도구', '수건걸이', '옷장정리', '서랍장수납', '책꽂이정리', '필기도구',
  '학용품세트', '사무용품', '캠핑장비세트', '수영복세트', '겨울용품', '여름용품', '정수기관리', '전기장판', '김치냉장고', '김치찌개',
  '된장찌개', '순두부찌개', '김치볶음밥', '제육볶음', '갈비구이', '삼겹살구이', '김밥세트', '라면정식', '잔치국수', '비빔냉면',
  '초밥세트', '우동정식', '쫄면정식', '순대국밥', '강아지산책', '고양이사료', '열대어수조', '애완동물', '동물보호소', '야생동물',
  '해양생물', '파충류관찰', '조류관찰', '곤충채집', '수족관견학', '동물원방문', '반려견용품', '반려묘용품', '동물병원', '애견미용실',
  '동물사료', '봄꽃나들이', '벚꽃구경', '단풍여행', '설경감상', '야생화관찰', '숲길산책', '해변산책', '해수욕장', '모래사장',
  '조약돌해변', '계곡피서', '폭포탐방', '동굴탐험', '천체관측', '별자리관찰', '일몰감상', '일출감상', '산들바람', '봄비내림',
  '가을바람', '첫눈관측', '지하철환승', '버스정류장', '택시승강장', '기차역사', '고속버스', '시외버스', '공항버스', '시내버스',
  '통근버스', '관광버스', '자전거도로', '자동차도로', '보행자도로', '횡단보도', '신호체계', '대형마트', '약국처방', '병원진료',
  '종합병원', '피부과진료', '내과진료', '치과진료', '미용실예약', '세탁소수선', '우체국창구', '은행창구', '베이커리', '공공도서관',
  '시민체육관', '동네공원', '어린이놀이터', '놀이공원', '전시회장', '박람회장', '연차휴가', '월차휴가', '병가신청', '육아휴직',
  '출산휴가', '복리후생', '성과급여', '야간수당', '교통비정산', '출장경비', '송년회식', '사내동호회', '직무교육', '신입연수',
  '인사평가', '승진심사', '퇴직관리', '그림그리기', '사진찍기', '요리배우기', '운동습관', '수영강습', '테니스레슨', '골프연습',
  '등산모임', '영화감상', '음악감상', '보드게임', '카드게임', '여행계획', '꽃꽂이교실', '서예연습', '도자기공예', '수채화그리기',
  '만화책감상', '뮤지컬관람', '고속철도역사', '시민체육공원', '빅데이터분석', '전사자원관리', '고객관계관리',
] as const;

// ============================================================
// HARD — 7~10글자 / 200점 (84개)
// ============================================================
export const HARD_WORDS = [
  '국립중앙박물관', '국립중앙도서관', '서울시립미술관', '자전거전용도로', '지하철환승통로', '반려동물용품점', '어린이교통안전', '전기자동차충전소', '가정용공기청정기', '종합건강검진센터',
  '유기농농산물매장', '성과관리시스템', '임직원교육과정', '유연근무시간제', '사내동호회활동', '예산집행계획서', '고객만족도조사', '업무프로세스개선', '개인정보보호교육', '산업안전보건교육',
  '직무능력향상과정', '연차휴가사용계획', '클라우드컴퓨팅', '마이크로서비스', '검색엔진최적화', '반응형웹디자인', '버전관리시스템', '프로그래밍언어', '정보보안전문가', '소프트웨어개발자',
  '인공지능알고리즘', '오픈소스라이선스', '사용자경험디자인', '모바일애플리케이션', '수제맥주전문점', '친환경식품전문점', '건강보조식품점', '아기용품전문점', '어린이전용도서관', '야생동물보호소',
  '해양생물수족관', '곤충생태학습원', '습지생태관찰로', '야생조류관찰대', '유기동물보호소', '반려동물훈련소', '천연기념물보호소', '산림휴양림관리', '지하철환승역사', '시외버스터미널',
  '전기차충전시설', '자율주행자동차', '공공자전거대여소', '지역문화예술회관', '청년창업지원센터', '종합문화센터시설', '다문화가족지원센터', '청소년수련관시설', '복합쇼핑몰시설', '성과평가관리시스템',
  '임직원복리후생제도', '사내동호회활동지원', '산업재해예방교육', '직장내괴롭힘예방', '성희롱예방교육', '정보보호서약서', '개인정보처리방침', '퇴직금산정기준서', '연봉협상자료집', '해외출장경비정산',
  '리더십교육과정', '사내멘토링제도', '조직문화개선활동', '서버가상화기술', '분산처리시스템', '네트워크보안관리', '사용자인증시스템', '지능형검색엔진', '실시간협업도구', '클라우드스토리지',
  '컴퓨터비전시스템', '자연어처리모델', '추천알고리즘기술', '머신러닝파이프라인',
] as const;

/** 난이도별 pool (engine 단어 생성용) */
export const WORD_POOLS: Record<Difficulty, readonly string[]> = {
  easy: EASY_WORDS,
  medium: MEDIUM_WORDS,
  hard: HARD_WORDS,
};

/** 난이도별 기본 점수 — 단일 출처 */
export const BASE_SCORE_BY_DIFFICULTY: Record<Difficulty, number> = {
  easy: 100,
  medium: 150,
  hard: 200,
};

/**
 * 화면 중복 회피 시 대체 pool 탐색 순서 (결정론적 고정 순서).
 */
export const DIFFICULTY_FALLBACK_ORDER: Record<Difficulty, readonly Difficulty[]> = {
  easy: ['easy', 'medium', 'hard'],
  medium: ['medium', 'easy', 'hard'],
  hard: ['hard', 'medium', 'easy'],
};

/**
 * 단어 → 난이도 역인덱스 (점수 산정 SSoT).
 */
const WORD_DIFFICULTY: ReadonlyMap<string, Difficulty> = (() => {
  const m = new Map<string, Difficulty>();
  for (const w of EASY_WORDS) m.set(w, 'easy');
  for (const w of MEDIUM_WORDS) m.set(w, 'medium');
  for (const w of HARD_WORDS) m.set(w, 'hard');
  return m;
})();

/** 등록된 단어의 난이도. 사전에 없으면 null (추정하지 않는다). */
export function getWordDifficulty(word: string): Difficulty | null {
  return WORD_DIFFICULTY.get(word) ?? null;
}

/**
 * 기본 점수 — **단어가 속한 난이도**로 결정한다.
 */
export function getBaseScore(word: string, difficulty?: Difficulty): number {
  const d = difficulty ?? WORD_DIFFICULTY.get(word);
  if (d) return BASE_SCORE_BY_DIFFICULTY[d];
  return BASE_SCORE_BY_DIFFICULTY.easy;
}

/**
 * @deprecated 글자 수 기반 분류. **점수 산정에 사용하지 말 것**.
 */
export function getWordLengthCategory(word: string): Difficulty {
  if (word.length <= 3) return 'easy';
  if (word.length <= 6) return 'medium';
  return 'hard';
}
